import React, { useEffect, useState, useRef } from 'react';
import { Map, OrderedMap } from 'immutable';
import TCell from '../../../frontend-common-libs/src/components/common/tables/TCell';
import LoadableTBody from '../../../frontend-common-libs/src/components/common/tables/LoadableTBody';
import { ImmutableMap, InstrumentItem } from '../../../frontend-common-libs/src/common/types';
import PrimaryButton from '../../../frontend-common-libs/src/common/buttons';
import styles from './styles/fleet.module.scss';
import { getCurrentUserIdentityId } from '../../../frontend-common-libs/src/utils/authUtils';
import Reservation from '../Reservation';
import { FleetToggleOptionsEnum } from '../FleetManagementOptions';
import InstrumentFleetModal from './InstrumentFleetModal';
import ReservationButtons from './ReservationButtons';
import Checkbox from '../../../frontend-common-libs/src/common/checkbox';
import ProtocolSelectionDropdown, { fetchProtocolsIfNeeded } from './ProtocolSelectionDropdown';
import { removeInstrumentAssignedProtocol } from './ReservationButtonsOnClickHandlers';
import renderInstrumentList, { InstrumentTypeEnum } from './InstrumentFleetTableHandlers';
import { FleetManagementTable } from '../fleet-management-table';

export type Props = {
  instruments: OrderedMap<string, Map<string, any>>;
  projectId: string;
  selectedFleetToggle: string;
  setFleetToggleCallback: (e: string) => void;
  statuses: Map<string, Map<string, any>>;
  fetchMoreData: (...args: Array<any>) => any;
  isLoading: boolean;
  isError: boolean;
};

export default function InstrumentFleetTable(props: Readonly<Props>) {
  const [selectedInstruments, setSelectedInstruments] = useState<Array<string>>([]);
  const [userHasReservations, setUserHasReservations] = useState<boolean>(false);
  const reservedInstruments = useRef<Array<string>>([]);
  const [displayReservationModal, setDisplayReservationModal] = useState<boolean>(false);
  const numberOfReservedInstruments = useRef(0);
  const [selectAllCheckbox, setSelectAllCheckbox] = useState<boolean>(false);
  const [editReservations, setEditReservations] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState('');
  const [reservationProtocol, setReservationProtocol] = useState<string>('[]');
  const [selectedProtocolId, setSelectedProtocolId] = useState('');

  const {
    instruments,
    projectId,
    selectedFleetToggle,
    setFleetToggleCallback,
    statuses,
    fetchMoreData,
    isLoading,
    isError
  } = props;

  const instrumentList = instruments.valueSeq().map(instrument => {
    const deviceId = instrument.get('deviceId');
    const deviceType = instrument.get('deviceType');
    const model = instrument.get('model');
    const name = instrument.get('name');
    const serial = instrument.get('serialNumber');
    const softwareVersion = instrument.get('ver');
    const details = instrument.get('details');
    const deviceStatus = statuses?.get(deviceId);
    let instrumentState;
    if (deviceStatus) {
      instrumentState = deviceStatus.getIn(['statusObject', 'state']);
    }

    return Map({
      id: deviceId,
      type: deviceType,
      model,
      name,
      serial,
      softwareVersion,
      details,
      instrumentState,
      deviceStatus
    }) as ImmutableMap<InstrumentItem>;
  });

  const clearSelection = () => {
    setSelectedInstruments([]);
    setSelectAllCheckbox(false);
  };

  useEffect(() => {
    clearSelection();
  }, [selectedFleetToggle]);

  const updateSelectedInstruments = (id: string, select: boolean): void => {
    const isInstrumentSelected = selectedInstruments.includes(id);
    setSelectedInstruments(previousSelection => {
      if (select && !isInstrumentSelected) {
        return [...previousSelection, id];
      }
      if (!select) {
        return previousSelection.filter(instrumentId => instrumentId !== id);
      }
      return previousSelection;
    });
  };

  const ptcTempoInstruments = instrumentList.filter(
    instrument => instrument.get('type') === InstrumentTypeEnum.ptcTempo
  );

  const updateReservedInstruments = (id: string, reserved: boolean) => {
    if (reserved && !reservedInstruments.current.includes(id)) {
      reservedInstruments.current.push(id);
    } else {
      reservedInstruments.current = reservedInstruments.current.filter(ele => ele !== id);
    }
    setUserHasReservations(reservedInstruments.current.length > 0);
  };

  const handleSelectAll = () => {
    setSelectAllCheckbox(!selectAllCheckbox);
  };

  const reserveSelectedInstruments = async (): Promise<void> => {
    if (selectedInstruments.length > 0) {
      setDisplayReservationModal(true);
      numberOfReservedInstruments.current = selectedInstruments.length;

      const updatedReservationProtocol = removeInstrumentAssignedProtocol(
        reservationProtocol,
        selectedInstruments
      );
      setReservationProtocol(updatedReservationProtocol);

      const userId: string = getCurrentUserIdentityId();
      await Reservation.bulkCreate(selectedInstruments, userId);
      clearSelection();
    }
  };

  const reserveButton = () => {
    const disableReserveButton = isError || selectedInstruments.length === 0;

    return (
      <div className={styles.buttonContainer}>
        <PrimaryButton
          id="reserve-button"
          className={styles.reserveButton}
          type="submit"
          isInProgress={isLoading}
          disabled={disableReserveButton}
          onClick={reserveSelectedInstruments}
        >
          Reserve
        </PrimaryButton>
      </div>
    );
  };

  const renderActionButtons = () => {
    if (!isLoading && selectedFleetToggle === FleetToggleOptionsEnum.Create) {
      return reserveButton();
    }

    if (!isLoading && selectedFleetToggle === FleetToggleOptionsEnum.Manage) {
      return (
        <ReservationButtons
          isError={isError}
          selectedInstruments={selectedInstruments}
          isLoading={isLoading}
          clearSelection={clearSelection}
          editReservations={editReservations}
          setEditReservations={setEditReservations}
          selectedProtocol={selectedProtocol}
          setSelectedProtocol={setSelectedProtocol}
          reservationProtocol={reservationProtocol}
          setReservationProtocol={setReservationProtocol}
          selectedProtocolId={selectedProtocolId}
        />
      );
    }

    return null;
  };

  const renderProtocolHeader = () => {
    return selectedFleetToggle === FleetToggleOptionsEnum.Manage ? <TCell>PROTOCOL</TCell> : null;
  };

  const renderSelectAllCheckbox = () => {
    return (
      <TCell>
        <Checkbox
          id="checkbox-select-all"
          name="checkbox-select-all"
          value={selectAllCheckbox}
          checked={selectAllCheckbox}
          onChange={handleSelectAll}
        />
      </TCell>
    );
  };

  const renderMessage = () => {
    if (selectedFleetToggle === FleetToggleOptionsEnum.Manage && !userHasReservations) {
      return (
        <div
          className="flex-column-container no-reservation-message"
          data-testid="no-reservation-message"
        >
          <p>No instruments are currently reserved.</p>
          <p>
            Go to the <strong>Create Reservation</strong> tab to reserve instruments.
          </p>
        </div>
      );
    }
    return null;
  };

  const closeReservationModalCallback = () => {
    setDisplayReservationModal(false);
    setFleetToggleCallback(FleetToggleOptionsEnum.Manage);
  };

  const renderReservedModalDialog = () => {
    return (
      <InstrumentFleetModal
        onClose={closeReservationModalCallback}
        show={displayReservationModal}
        numberOfReservedInstruments={numberOfReservedInstruments.current}
      />
    );
  };

  const updateSelectedProtocol = (protocol: { id: string; name: string }) => {
    setSelectedProtocolId(protocol.id);
    setSelectedProtocol(protocol.name);
  };

  const protocols = fetchProtocolsIfNeeded(projectId);

  const isTableHidden = () => {
    return selectedFleetToggle === FleetToggleOptionsEnum.Manage && !userHasReservations;
  };

  const renderAdditionalHeaders = () => {
    return (
      <>
        <TCell>STATUS</TCell>
        {renderProtocolHeader()}
      </>
    );
  };

  return (
    <div className="flex-column-container" id="instruments-fleet-table-container">
      {renderReservedModalDialog()}
      {renderMessage()}
      <div id="instruments-fleet-manage-container">
        <FleetManagementTable isHidden={isTableHidden()}>
          {renderSelectAllCheckbox()}
          {renderAdditionalHeaders()}
          <LoadableTBody
            fetchMoreData={fetchMoreData}
            hasMoreData={false}
            isLoading={isLoading}
            isError={isError}
            errorMsg="Error retrieving instrument information."
          >
            {renderInstrumentList(
              ptcTempoInstruments,
              selectedFleetToggle,
              selectAllCheckbox,
              updateSelectedInstruments,
              updateReservedInstruments,
              reservationProtocol,
              setReservationProtocol
            )}
          </LoadableTBody>
        </FleetManagementTable>
        {selectedFleetToggle === FleetToggleOptionsEnum.Manage && editReservations ? (
          <ProtocolSelectionDropdown
            // @ts-ignore
            protocols={protocols}
            selectedProtocol={selectedProtocol}
            updateSelectedProtocol={updateSelectedProtocol}
          />
        ) : null}
      </div>
      {renderActionButtons()}
    </div>
  );
}
